import { useState } from "react";

import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";

import { InternalButton } from "@sharedComponents/buttons";

import ProductDetailProvider from "@contexts/product-detail-context";

import VariantSelector from "@components/store/product-detail/shared/variant-selector";

import { getVariantsExcludingMaster } from "@helpers/product-details";
import { getMasterVariant } from "@helpers/products/search";

import useBatchPopulateItems from "@hooks/cart/use-batch-populate-items";

import Modal from "@stadium/ui/src/components/ModalNew";
import {
  ModalHeader,
  ModalFooter,
  CloseButton,
  ModalBody,
  ModalContent,
  ModalDialog,
} from "@stadium/ui/src/components/ModalNew/utils";

import { useCartListingContext } from "@contexts/cart-listing-context";

import { ITEM_PRODUCT_TYPES, ORDER_TYPES } from "@constants/orders/cart";

import styles from "./main.module.scss";

const emptyObj = {};

const SendToManyVariantSelectorModal = () => {
  const { addItemInCart, loading } = useBatchPopulateItems({
    type: ORDER_TYPES.organizer,
  });

  const {
    closeSwagVariantSelectorModal,
    variantSelectionError,
    setVariantSelectionError,
    getSwagVariantsSelectionProducts,
  } = useCartListingContext();

  const [selectedVariantsData, setSelectedVariantsData] = useState(emptyObj);
  const [showError, setShowError] = useState("");

  const variantSelectionProducts = getSwagVariantsSelectionProducts();

  const mappedVariantSelectionProducts = variantSelectionProducts.map((data) => ({
    ...data,
    ...data.productData,
    variantsIncludingMaster: data.swagVariantFromCatalogStore,
    errorId: data.id,
  }));

  const getPayloadData = () => {
    const payloadData = mappedVariantSelectionProducts.reduce((productMapping, product) => {
      const {
        id,
        quantity: productQty,
        masterVariant: productVariant,
        variantsIncludingMaster = [],
        zoneId,
        storeId,
        vendorByCountry,
        productId,
        minOrderableQuantity,
        bulkAvailable,
      } = product || {};
      const masterVariant = getMasterVariant(variantsIncludingMaster);
      const variantsExcludingMaster = getVariantsExcludingMaster(variantsIncludingMaster);
      const selectedVariant =
        selectedVariantsData[id] || variantsExcludingMaster[0] || masterVariant || productVariant;

      const { externalId, priceIn, dietaryPreference } = selectedVariant;

      const quantity = (() => {
        if (bulkAvailable) return minOrderableQuantity;
        return productMapping[externalId] ? 1 : productQty;
      })();

      const commonAttribute = {
        product_type: ITEM_PRODUCT_TYPES.product,
        vendor_id: vendorByCountry?.externalId,
        zone_id: zoneId,
        store_id: storeId,
      };

      productMapping[externalId] = {
        product_id: externalId,
        price: priceIn,
        quantity,
        dietary_preference: dietaryPreference,
        ...commonAttribute,
      };
      productMapping[productId] = {
        product_id: productId.toString(),
        quantity: 0,
        ...commonAttribute,
      };
      return productMapping;
    }, {});

    return Object.values(payloadData);
  };

  const updateOrder = () => {
    const payloadData = getPayloadData();
    addItemInCart(payloadData).then(() => {
      closeSwagVariantSelectorModal();
      const omitSelectedLineItem = mappedVariantSelectionProducts.map(({ errorId }) => errorId);
      setVariantSelectionError({ ...omit(variantSelectionError, omitSelectedLineItem) });
    });
  };

  const handleSubmit = () => {
    const isErrorPresent = mappedVariantSelectionProducts.some(
      ({ id }) => selectedVariantsData[id]?.error,
    );

    if (isErrorPresent) {
      setShowError(true);
      return;
    }

    if (showError) {
      setShowError(false);
    }
    updateOrder();
  };

  const renderProductListing = (product) => (
    <ProductDetailProvider key={product.id}>
      <VariantSelector
        showPreferencesInput
        product={product}
        productId={product.id}
        showError={showError}
        selectedVariantsData={selectedVariantsData}
        setSelectedVariantsData={setSelectedVariantsData}
      />
    </ProductDetailProvider>
  );

  const renderVariantSelectorModal = () => {
    if (isEmpty(mappedVariantSelectionProducts)) {
      closeSwagVariantSelectorModal();
      return null;
    }
    return (
      <Modal closeModal={closeSwagVariantSelectorModal} newModal mobileBottomDrawer>
        <ModalDialog className={styles.sendToManyModal}>
          <ModalContent>
            <ModalHeader>
              <div>
                <div>Update for items in your bag</div>
                <div className={styles.modalSubHeading}>
                  We need more information for the following items before you check out.
                </div>
              </div>
              <CloseButton onClick={closeSwagVariantSelectorModal} />
            </ModalHeader>
            <ModalBody className={styles.modalBody}>
              {mappedVariantSelectionProducts.map(renderProductListing)}
            </ModalBody>
            <ModalFooter>
              <InternalButton isLoading={loading} styleType="primary" onClick={handleSubmit}>
                Update Bag
              </InternalButton>
            </ModalFooter>
          </ModalContent>
        </ModalDialog>
      </Modal>
    );
  };

  return renderVariantSelectorModal();
};
export default SendToManyVariantSelectorModal;
